import React from "react";
import ReactECharts from "echarts-for-react";

interface BarChartData {
  name: string;
  values: number[];
  color?: string;
}

interface LineChartData {
  name: string;
  values: number[];
  color: string;
}

export interface MultiBarLineChartProps {
  labels: string[];
  barDatasets: BarChartData[];
  lineDatasets: LineChartData[];
  showLabels?: boolean;
  showMinLine?: boolean;
}



const MultiBarLineChartECharts: React.FC<MultiBarLineChartProps> = ({
  labels,
  barDatasets,
  lineDatasets,
  showLabels = true,
  showMinLine = false,
}) => {
  // Capitalize labels
  const capitalizedLabels = labels.map(
    (label: string) =>
      label
        .split("_") // Split the label by underscores
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
        .join(" ") // Join the words with spaces
  );

  // Format large numbers to millions/thousands
  const formatValue = (value: number, asPercentage: boolean = false): string => {
    if (asPercentage) {
      return `${value.toFixed(2)}%`;
    }
  
    if (value >= 1_000_000) {
      return (value / 1_000_000).toFixed(1) + "M";
    } else if (value >= 1_000) {
      return (value / 1_000).toFixed(1) + "K";
    }
  
    return value.toString();
  };

  const isPercentage = barDatasets.every(dataset =>
    dataset.values.every(value => value <= 100)
  );

  // Create the series for the bar chart
  const barSeries = barDatasets.map((dataset, index) => {
    // Create a gradient of blues for bars
    const colors = [
      "#143969", // adrsta-blue (primary)
      "#4B7FBF", // lighter shade
      "#6FA2EA", // even lighter
      "#94C5FF", // lightest
      "#B8D8FF", // very light blue
    ];

    return {
      name: dataset.name,
      type: "bar",
      data: dataset.values,
      itemStyle: {
        color: colors[index % colors.length],
        borderRadius: [3, 3, 0, 0], // Rounded top corners
      },
      ...(showMinLine && {
        markLine: {
          silent: true,
          symbol: "none",
          lineStyle: {
            color: "#143969",
            type: "dashed",
            width: 1,
          },
          data: [
            {
              type: "min",
              label: {
                show: false,
              },
            },
          ],
        },
      }),
      label: {
        show: false,
        position: "top",
        formatter: (params: any) => {
          const value = params.value;
          const formattedValue = isPercentage ? `${value.toFixed(2)}%` : value.toLocaleString();
          return `${params.seriesName}: ${formattedValue}`;
        },
        fontSize: 12,
        fontWeight: "bold",
        color: "#143969",
        distance: 5,
        rotate: 0,
        align: "left",
        verticalAlign: "middle",
      },
      barMaxWidth: 50, // Limit maximum width of bars
    };
  });

  // Create the series for the line chart
  const lineSeries = lineDatasets.map((dataset) => ({
    name: dataset.name,
    type: "line",
    data: dataset.values,
    itemStyle: {
      color: "#143969",
    },
    lineStyle: {
      color: "#143969",
      width: 2,
    },
    symbol: "circle",
    symbolSize: 6,
  }));

  // Combine bar and line series
  const series = [...barSeries, ...lineSeries];

  const option = {
    legend: {
      orient: 'vertical',
      top: 'top',
      right: 2,
      textStyle: {
        fontSize: 16,
      },
    },
    grid: {
      left: "50px",
      right: "50px",
      bottom: "50px",
      top: "50px",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: capitalizedLabels, // Use the transformed labels
      axisLabel: {
        rotate: 0,
        interval: 0,
        color: "#143969",
        fontSize: 12,
        margin: 8,
        align: "middle",
      },
      axisLine: {
        lineStyle: {
          color: "#143969",
          width: 1,
        },
      },
    },
    yAxis: {
      type: "value",
      minInterval: 1,
      name: "Value",
      axisLabel: {
        formatter: (value: number) => formatValue(value, isPercentage),
      },
      axisLine: {
        lineStyle: {
          color: "#143969",
          width: 1,
        },
      },
      splitLine: {
        lineStyle: {
          color: "#E5E7EB",
        },
      },
    },
    series,
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
      },
      formatter: (params: any) => {
        // Get the category name (channel name)
        const categoryName = params[0].name;

        // Create tooltip content
        let tooltipContent = `<div style="font-weight: bold; margin-bottom: 5px;">${categoryName}</div>`;

        // Add each series data
        params.forEach((param: any) => {
          const seriesName = param.seriesName;
          const value = param.value;
          const color = param.color;
          let newName: string = "";
          // Format the value based on the series name
          let formattedValue = value;
          if (seriesName === "Adstock" || seriesName === "Theta") {
            // For adstock/theta values (typically between 0-1)
            newName = "Theta";
            formattedValue = value.toFixed(2);
          } else if (seriesName === "Strength" || seriesName === "Alpha") {
            // For strength/alpha values
            newName = "Alpha";
            formattedValue = value.toFixed(2);
          } else if (seriesName === "Length" || seriesName === "Gamma") {
            // For length/gamma values
            newName = "Gamma";
            formattedValue = value.toFixed(2);
          } else {
            newName = seriesName;
            // For other values, use standard formatting
            formattedValue = formatValue(value, isPercentage);
          }

          tooltipContent += `
            <div style="display: flex; justify-content: space-between; align-items: center; margin: 3px 0;">
              <span style="display: inline-block; width: 10px; height: 10px; background-color: ${color}; margin-right: 5px;"></span>
              <span style="flex-grow: 1;">${newName}:</span>
              <span style="font-weight: bold;">${formattedValue}</span>
            </div>
          `;
        });

        return tooltipContent;
      },
      textStyle: {
        color: "#000000",
        fontSize: 12,
      },
      extraCssText: "max-width: 250px; white-space: normal;",
    },
  };

  return (
    <ReactECharts option={option} style={{ height: "450px", width: "100%" }} />
  );
};

export default MultiBarLineChartECharts;
